import React, { useState, useEffect, useMemo } from "react";
import AppSDK from "@growthos/ui-app-sdk";
import BookFiltersStores from "../../../Flux/Stores/BookFiltersStore";
import AppConfig from "../../../AppConfig";
import MasterCampaignsStore from "../../../Flux/Stores/MasterCampaignsStore";
import WorkbookStore from "../../../Flux/Stores/WorkbookStore";
import BookFiltersStore from "../../../Flux/Stores/BookFiltersStore";
import Button, { BUTTON_DISPLAY, BUTTON_SIZE } from "@acadia-ui/button";

import PropTypes from "prop-types";
import Popover from "@acadia-ui/popover";

const ExportOptions = (props) => {
	const downloadFile = async (url, fileName) => {
		const contextId = AppSDK.getContextId();
		const urlWithContextId = url + (contextId ? `?contextID=${contextId}` : "");

		try {
			const response = await AppSDK.fetchMultiCloud(urlWithContextId);
			let formattedFileName = fileName;
			const contentDisposition = response.headers["content-disposition"];
			if (contentDisposition && contentDisposition.indexOf("attachment") !== -1) {
				const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
				const matches = filenameRegex.exec(contentDisposition);
				if (matches !== null && matches[1]) {
					formattedFileName = matches[1].replace(/['"]/g, "");
				}
			}

			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", formattedFileName);
			document.body.appendChild(link);
			link.click();
		} catch (error) {
			console.error("Error downloading file:", error);
		}
	};

	const returnMenuItem = (label, value, platformExportAPI) => (
		<Button
			// this comment exists to prevent eslint from making this unreadable.
			key={`${label}_${value}`}
			display={BUTTON_DISPLAY.TEXT}
			size={BUTTON_SIZE.SMALL}
			icon="icon-download"
			onClick={(e) => handleGenericWorkflowExport(e, label, value, platformExportAPI)}
			data-tracking-id-alt={`export-${label}`}
		>
			{label}
		</Button>
	);

	const returnAdvertmindMenuItem = (campaignID) => (
		<Button
			// this comment exists to prevent eslint from making this unreadable.
			key={`Advertmind_${campaignID}`}
			display={BUTTON_DISPLAY.TEXT}
			size={BUTTON_SIZE.SMALL}
			icon="icon-download"
			onClick={(e) => handleAdvertmindExport(campaignID)}
			data-tracking-id-alt={`export-advertmind`}
		/>
	);

	const buildExportDropDown = useMemo(() => {
		const { PlatformName, PlatformID, PlatformExportAPI, RegionName, CampaignUniqueIDCode, PlatformCampaignPK } = props.metaData;

		const isTrafficEnabled = WorkbookStore.getIsTrafficEnabled();

		const dropDownData = [returnMenuItem("Taxonomy Workbook", 0)];

		if (PlatformID > 0) {
			if (props.templateExists === 1) {
				dropDownData.push(returnMenuItem(PlatformName, PlatformID, PlatformExportAPI));
			}
			if (RegionName === "Latin America" && props.metaData.PlatformName === "Google Campaign Manager") {
				dropDownData.push(returnAdvertmindMenuItem(CampaignUniqueIDCode));
			}
		}

		// try, catch in case the PlatformCampaignPK is not valid JSON
		try {
			if (PlatformCampaignPK && JSON.parse(PlatformCampaignPK)?.length > 0 && isTrafficEnabled) {
				dropDownData.push(returnMenuItem("Unexpected Change", 1));
			}
		} catch (e) {
			// noop
		}

		return dropDownData;
	}, [props.metaData, props.templateExists, props.auditLevelID]);

	const handleGenericWorkflowExport = (event, platformName, platformID, platformExportAPI) => {
		const { ApiBasePath, TraffickingApiBasePath } = AppConfig?.getBookApiBasePath?.();
		const { platform } = BookFiltersStores?.getChoicesState?.();
		const contextValues = BookFiltersStore.getContextValues();
		const parentCampaignUniqueID = MasterCampaignsStore?.getParentCampaignUniqueID?.();
		if (platformExportAPI) {
			downloadFile(
				`${ApiBasePath}/plook/ExcelReport/${platformExportAPI}/${platform.value}/${props.auditLevelID}/${parentCampaignUniqueID}`,
				`${props.metaData.CampaignDescription}_${parentCampaignUniqueID}-${platform.label} Export.xlsx`
			);
		} else {
			switch (platformID) {
				case 0:
					downloadFile(
						`${ApiBasePath}/plook/ExcelReport/GetGenericExport/${contextValues.contextId}/${contextValues?.agency?.name}/${contextValues?.client?.name}/${contextValues?.region?.name}/${contextValues?.country?.name}/${platform.value}/${props.auditLevelID}/${parentCampaignUniqueID}`,
						`${props.metaData.CampaignDescription}_${parentCampaignUniqueID} Taxonomy Workbook Export.xlsx`
					);
					break;
				case 1:
					downloadFile(
						`${TraffickingApiBasePath}plook/Campaign/GetCFChangeReport/${parentCampaignUniqueID}`,
						`${props.metaData.CampaignDescription}_${parentCampaignUniqueID}-Unexpected Change Export.xlsx`
					);
					break;
				// case 5:
				// 	downloadFile(
				// 		`${TraffickingApiBasePath}plook/ExcelReport/GetGenericExport/${contextValues.contextId}/${contextValues?.agency?.name}/${contextValues?.client?.name}/${contextValues?.region?.name}/${contextValues?.country?.name}/${platform.value}/${auditlevel.value}/${parentCampaignUniqueID}`,
				// 		`${props.metaData.CampaignDescription}_${parentCampaignUniqueID}-Trafficking Sheet Export.xlsx`
				// 	);
				// 	break;
				default:
					break;
			}
		}
	};

	const handleAdvertmindExport = (campaignID) => {
		const { ApiBasePath } = AppConfig?.getBookApiBasePath?.() || {};
		downloadFile(`${ApiBasePath}/plook/ExcelReport/GetAdvertmindExport/${campaignID}`, `${props.metaData.CampaignDescription}_${campaignID}-Advertmind Export.xlsx`);
	};

	return (
		<Popover
			trigger={
				<Button display={BUTTON_DISPLAY.ICON_BORDERED} icon="icon-more" disabled={props.auditLevelID == null} data-tracking-id-alt="export-options-popover-trigger">
					more options
				</Button>
			}
		>
			{buildExportDropDown}
			{props.children}
		</Popover>
	);
};

export default ExportOptions;

ExportOptions.propTypes = {
	metaData: PropTypes.shape({
		PlatformName: PropTypes.string,
		PlatformID: PropTypes.number,
		PlatformExportAPI: PropTypes.string,
		RegionName: PropTypes.string,
		CampaignUniqueIDCode: PropTypes.string,
		PlatformCampaignPK: PropTypes.string,
		CampaignDescription: PropTypes.string
	}),
	templateExists: PropTypes.number,
	auditLevelID: PropTypes.number,
	children: PropTypes.node
};
